<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCsmlResizable } from '../../composables/useCsmlResizable';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { getOutPorts } from '../../composables/useCsmlPorts';
import { nodeTemplates } from '../../constants/csmlNodeTemplates';
import { getCategoryStyle } from '../../constants/csmlNodeCategories';

const props = defineProps({
  connection: { type: Object, required: true },
  nodes: { type: Array, default: () => [] },
});

const emit = defineEmits(['reconnect', 'delete', 'close']);

const { width, startResize } = useCsmlResizable('csml-builder-panel-width', {
  min: 280,
  max: 480,
  initial: 320,
});

const { t, te } = useI18n();

const nodeLabel = node => {
  const key = `AGENT_BOTS.VISUAL_BUILDER.NODES.${String(
    node.type
  ).toUpperCase()}.LABEL`;
  return te(key) ? t(key) : node.label;
};

const describe = id => {
  const node = props.nodes.find(n => n.id === id);
  if (!node) return null;
  return {
    node,
    label: nodeLabel(node),
    icon: nodeTemplates[node.type]?.icon || 'i-lucide-box',
    style: getCategoryStyle(node.type),
  };
};

const from = computed(() => describe(props.connection.fromId));
const to = computed(() => describe(props.connection.toId));

const branch = computed(() => {
  if (!from.value) return '';
  const ports = getOutPorts(from.value.node);
  if (ports.length < 2) return '';
  const port = ports.find(p => p.idx === (props.connection.fromPortIdx ?? 0));
  if (!port) return '';
  return port.labelKey
    ? t(`AGENT_BOTS.VISUAL_BUILDER.PORTS.${port.labelKey}`)
    : port.label;
});
</script>

<template>
  <aside
    class="relative flex flex-col max-h-full overflow-hidden border shadow-lg pointer-events-auto rounded-xl border-n-weak bg-n-solid-1/95 backdrop-blur"
    :style="{ width: `${width}px` }"
  >
    <div
      class="absolute inset-y-0 z-10 w-1.5 cursor-col-resize start-0 hover:bg-n-brand/30"
      @mousedown="startResize"
    />
    <header
      class="flex items-center justify-between gap-2 px-3 py-2.5 border-b shrink-0 border-n-weak"
    >
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('AGENT_BOTS.VISUAL_BUILDER.CONNECTION.TITLE') }}
      </h3>
      <Button
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-x"
        is-icon-only
        @click="emit('close')"
      />
    </header>

    <div class="flex flex-col gap-4 p-3">
      <template v-for="end in ['from', 'to']" :key="end">
        <div v-if="end === 'from' ? from : to" class="flex flex-col gap-1">
          <span class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t(`AGENT_BOTS.VISUAL_BUILDER.CONNECTION.${end.toUpperCase()}`) }}
          </span>
          <div
            class="flex items-center gap-2 px-2.5 py-2 border rounded-lg border-n-weak bg-n-alpha-1"
          >
            <span
              class="flex items-center justify-center rounded-md size-6 shrink-0"
              :class="(end === 'from' ? from : to).style.chip"
            >
              <Icon
                :icon="(end === 'from' ? from : to).icon"
                class="size-3.5"
              />
            </span>
            <span class="text-sm truncate text-n-slate-12">
              {{ (end === 'from' ? from : to).label }}
            </span>
          </div>
        </div>
      </template>

      <div v-if="branch" class="flex flex-col gap-1">
        <span class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('AGENT_BOTS.VISUAL_BUILDER.CONNECTION.BRANCH') }}
        </span>
        <p class="text-sm text-n-slate-11">{{ branch }}</p>
      </div>
    </div>

    <footer
      class="flex items-center gap-2 px-3 py-2 border-t shrink-0 border-n-weak"
    >
      <Button
        class="flex-1"
        size="sm"
        variant="faded"
        color="slate"
        icon="i-lucide-cable"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.CONNECTION.RECONNECT')"
        @click="emit('reconnect')"
      />
      <Button
        class="flex-1"
        size="sm"
        variant="faded"
        color="ruby"
        icon="i-lucide-trash-2"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.REMOVE')"
        @click="emit('delete')"
      />
    </footer>
  </aside>
</template>
